<template>
  <!--
    AB#21914 — "Edit tech pack" is a split button while the V2 editor and the
    classic (V1, admin.custimoo.com) editor coexist: the main part opens the
    new view, the chevron offers both by name so the desk can see there IS a
    new view and can fall back to the old one for a while.
  -->
  <div
    class="tpe"
    :class="`tpe--${mode}`"
    :title="mode === 'icon' ? 'Edit techpack' : undefined"
    :aria-label="mode === 'icon' ? 'Edit techpack' : undefined"
  >
    <a
      :href="newHref"
      class="tpe-main"
      :class="mainClass"
      :title="
        mode === 'icon'
          ? `Edit techpack (new view)${lastUpdated ? ` — ${lastUpdated}` : ''}`
          : lastUpdated || undefined
      "
      aria-label="Edit techpack (new view)"
      data-test="techpack-edit-new"
    >
      <PencilSquareIcon class="tpe-ic" aria-hidden="true" />
      <span v-if="mode === 'text'">Edit tech pack</span>
      <span v-if="mode === 'text'" class="tpe-badge">new</span>
    </a>
    <Dropdown
      :trigger-style="chevClass"
      custom-class="tpe-dd"
      trigger-aria-label="Choose the tech pack editor"
    >
      <template #trigger>
        <ChevronDownIcon class="tpe-chev" aria-hidden="true" />
      </template>
      <template #contents>
        <div class="tpe-menu" role="menu">
          <!-- AB#22185 (Zendesk #9938): who last updated the tech pack. -->
          <p v-if="lastUpdated" class="tpe-stamp" data-test="techpack-last-updated">
            {{ lastUpdated }}
          </p>
          <a :href="newHref" class="tpe-opt" role="menuitem" data-test="techpack-edit-menu-new">
            <b>New view</b><span>redesigned editor, dark mode, phone-friendly</span>
          </a>
          <a
            :href="classicUrl"
            class="tpe-opt"
            role="menuitem"
            data-test="techpack-edit-menu-classic"
          >
            <b>Classic view</b><span>the previous editor, unchanged</span>
          </a>
        </div>
      </template>
    </Dropdown>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { ChevronDownIcon, PencilSquareIcon } from '@heroicons/vue/24/outline';
import Dropdown from '@/components/Shared/Dropdown.vue';

const props = withDefaults(
  defineProps<{
    orderId: number | string;
    /** `icon` on the order action bar; `text` where a labelled button fits. */
    mode?: 'icon' | 'text';
    /** Classes for the main link when the host wants its own button style. */
    mainClass?: string;
    /** Classes for the chevron trigger. */
    chevClass?: string;
    /** AB#22185: "Tech pack last updated by Name, date" — tooltip and menu line. */
    lastUpdated?: string;
  }>(),
  { mode: 'text', mainClass: '', chevClass: '', lastUpdated: '' }
);

// Resolved through the router when one is mounted (the app); a plain path
// otherwise (specs mount the action bar without a router).
const router = useRouter();
const newHref = computed(() => {
  try {
    return (
      router?.resolve({ name: 'edit-techpack', params: { orderId: String(props.orderId) } }).href ??
      `/edit-techpack/${props.orderId}`
    );
  } catch {
    return `/edit-techpack/${props.orderId}`;
  }
});

const classicUrl = computed(
  () => `${import.meta.env.VITE_VUE2_APP_URL}/edit-techpack/${props.orderId}?source=new_dashboard`
);
</script>

<style scoped>
.tpe {
  display: inline-flex;
  align-items: stretch;
}
.tpe-main {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  text-decoration: none;
}
.tpe--text .tpe-main {
  border: 1px solid var(--accent);
  color: var(--accent);
  border-radius: 8px 0 0 8px;
  padding: 6px 10px;
  font-weight: 500;
}
.tpe--text :deep(.tpe-dd .dropdown-trigger) {
  border: 1px solid var(--accent);
  border-left: 0;
  color: var(--accent);
  border-radius: 0 8px 8px 0;
  padding: 6px 6px;
  background: transparent;
  display: inline-flex;
  align-items: center;
  height: 100%;
}
.tpe--icon :deep(.tpe-dd .dropdown-trigger) {
  display: inline-flex;
  align-items: center;
  height: 100%;
  padding: 0 4px;
  background: transparent;
  border: 0;
  color: var(--ink-soft);
}
.tpe-ic {
  width: 20px;
  height: 20px;
}
.tpe-chev {
  width: 14px;
  height: 14px;
}
.tpe-badge {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 1px 6px;
  border-radius: 999px;
  background: var(--accent-soft);
  color: var(--accent);
}
.tpe-menu {
  display: flex;
  flex-direction: column;
  min-width: 240px;
  padding: 6px;
  border: 1px solid var(--border);
  border-radius: 10px;
}
.tpe-stamp {
  margin: 0 0 4px;
  padding: 6px 10px 8px;
  border-bottom: 1px solid var(--border-soft);
  font-size: 11.5px;
  color: var(--ink-mute);
}
.tpe-opt {
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: 8px 10px;
  border-radius: 8px;
  color: var(--ink);
  text-decoration: none;
  font-size: 13px;
}
.tpe-opt span {
  font-size: 11.5px;
  color: var(--ink-mute);
}
.tpe-opt:hover {
  background: var(--surface-2);
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
}
</style>
